import React, { JSX, ReactNode, useCallback } from 'react';

import { Helmet } from 'react-helmet';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import AccommodationPropertyStateIndicator from '~/components/Accommodation/Pages/Properties/components/AccommodationPropertyStateIndicator';
import AccommodationPropertyRatesSection from '~/components/Accommodation/Pages/PropertyDetails/sections/AccommodationPropertyRates/AccommodationPropertyRatesSection';
import ClassicPageHeader from '~/components/Common/Elements/PageHeader';
import PageBody from '~/components/Page/PageBody';
import PageHeaderBar from '~/components/Page/PageHeaderBar';
import PageLayout from '~/components/Page/PageLayout';
import PageSidebar from '~/components/Page/PageSidebar';

import { getProperty, getPropertyContent } from '~/services/AccommodationService';

import { useAccommodationServiceFetch } from '../../hooks/useAccommodationServiceFetch';
import AccommodationLinksPanel from '../Properties/components/AccommodationLinksPanel';

import AccommodationPropertyDebugModal from './AccommodationPropertyDebugModal';
import AccommodationPropertyDetailsNav from './AccommodationPropertyDetailsNav';
import AccommodationPropertyDetailsOverviewPane from './AccommodationPropertyDetailsOverviewPane';
import AccommodationPropertyDetailsSidebar from './AccommodationPropertyDetailsSidebar';
import AccommodationPropertyRoomDetailsPage from './AccommodationPropertyRoomDetailsPage';
import AccommodationPropertyDetailsContextProvider from './context/AccommodationPropertyDetailsContext';
import AccommodationPropertyContentSelection from './sections/AccommodationPropertyDetails/AccommodationPropertyContentSelection';
import AccommodationPropertyDetailsSection from './sections/AccommodationPropertyDetails/AccommodationPropertyDetailsSection';
import AccommodationPropertyGeneratedContent from './sections/AccommodationPropertyDetails/AccommodationPropertyGeneratedContent';
import AccommodationPropertyFacilitiesSection from './sections/AccommodationPropertyFacilities/AccommodationPropertyFacilitiesSection';
import AccommodationPropertyImagesSection from './sections/AccommodationPropertyImages/AccommodationPropertyImagesSection';
import AccommodationPropertyRoomAmenitiesSection from './sections/AccommodationPropertyRoomAmenities/AccommodationPropertyRoomAmenitiesSection';
import AccommodationPropertyRoomImagesSection from './sections/AccommodationPropertyRoomImages/AccommodationPropertyRoomImagesSection';
import AccommodationPropertyRoomMappingSection from './sections/AccommodationPropertyRoomMapping/AccommodationPropertyRoomMappingSection';
import AccommodationPropertyRoomsSection from './sections/AccommodationPropertyRooms/AccommodationPropertyRoomsSection';
import AccommodationPropertySuppliersSection from './sections/AccommodationPropertySuppliers/AccommodationPropertySuppliersSection';
import useAccommodationPropertyLayoutPreference, {
  ACCOMMODATION_PROPERTY_LAYOUTS,
  AccommodationPropertyLayout,
} from './useAccommodationPropertyLayoutPreference';

interface LayoutProps {
  property: App.Property | any;
  routes: Array<{ path?: string; component: JSX.Element; index?: true }>;
  layout: AccommodationPropertyLayout;
  onLayoutChange: (layout: AccommodationPropertyLayout) => void;
}

function AccommodationPropertyLayoutToggle({ layout, onLayoutChange }: Pick<LayoutProps, 'layout' | 'onLayoutChange'>) {
  return (
    <ToggleButtonGroup
      value={layout}
      exclusive
      size="small"
      aria-label="Property page layout"
      onChange={(_, nextLayout: AccommodationPropertyLayout | null) => {
        if (nextLayout) {
          onLayoutChange(nextLayout);
        }
      }}
    >
      <ToggleButton value={ACCOMMODATION_PROPERTY_LAYOUTS.CLASSIC}>Classic</ToggleButton>
      <ToggleButton value={ACCOMMODATION_PROPERTY_LAYOUTS.SIDEBAR}>Sidebar</ToggleButton>
    </ToggleButtonGroup>
  );
}

function PropertyHeaderTitle({ propertyName }: { propertyName?: ReactNode }) {
  return (
    <Box component="span" display="inline-flex" alignItems="center" gap={1.5}>
      <IconButton
        sx={{ border: 1 }}
        size="small"
        color="secondary"
        title="Back"
        component={Link}
        to="/accommodation/properties"
      >
        <ArrowBackIcon />
      </IconButton>
      <Typography component="span" variant="h5">
        {propertyName || 'Property'}
      </Typography>
    </Box>
  );
}

type ActiveBundleChip = { id: string; name: string | null };
type ActiveBundleReference = string | ActiveBundleChip;

function normaliseActiveBundleChip(bundle: ActiveBundleReference): ActiveBundleChip {
  if (typeof bundle === 'string') {
    return { id: bundle, name: null };
  }

  return { id: bundle.id, name: bundle.name ?? null };
}

function PropertyHeaderActions({ property, layout, onLayoutChange }: LayoutProps) {
  const activeBundles =
    property.activeBundles ??
    property.activeBundleIds?.map((bundle: ActiveBundleReference) => normaliseActiveBundleChip(bundle)) ??
    [];

  return (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
      <AccommodationPropertyLayoutToggle layout={layout} onLayoutChange={onLayoutChange} />
      {property.indicativePropertyState && <AccommodationPropertyStateIndicator property={property} />}
      {activeBundles.map((bundle: ActiveBundleChip) => (
        <Tooltip key={bundle.id} title={`Open ${bundle.name ?? `active bundle ${bundle.id.slice(0, 8)}`}`}>
          <Chip
            icon={<LocalOfferIcon />}
            label={bundle.name ?? `Active bundle ${bundle.id.slice(0, 8)}`}
            color="success"
            variant="outlined"
            size="small"
            clickable
            component={Link}
            to={`/accommodation/bundles/${bundle.id}/hotels`}
          />
        </Tooltip>
      ))}
      <AccommodationPropertyDebugModal />
      <AccommodationLinksPanel property={property} />
    </Stack>
  );
}

function ClassicPropertyLayout({ property, routes, layout, onLayoutChange }: LayoutProps) {
  return (
    <Container maxWidth="xl">
      <Stack direction="column" gap={4}>
        <Stack direction="column">
          <ClassicPageHeader title={property.name} backButton="/accommodation/properties">
            <PropertyHeaderActions
              property={property}
              routes={routes}
              layout={layout}
              onLayoutChange={onLayoutChange}
            />
          </ClassicPageHeader>
          <AccommodationPropertyDetailsOverviewPane />
        </Stack>

        <Box flexShrink={0}>
          <Card sx={{ position: 'sticky', top: 16 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <AccommodationPropertyDetailsNav />
            </Box>
          </Card>
        </Box>
        <Stack direction="column" flexGrow={1}>
          <Routes>
            {routes.map(({ path, component, index }) => (
              <Route index={index} path={path} element={component} key={path ?? 'index'} />
            ))}
          </Routes>
        </Stack>
      </Stack>
    </Container>
  );
}

function SidebarPropertyLayout({ property, routes, layout, onLayoutChange }: LayoutProps) {
  return (
    <PageLayout hasLeftSidebar>
      <PageHeaderBar
        title={<PropertyHeaderTitle propertyName={property.name} />}
        actions={
          <PropertyHeaderActions property={property} routes={routes} layout={layout} onLayoutChange={onLayoutChange} />
        }
      />
      <PageSidebar placement="start" width="medium">
        <AccommodationPropertyDetailsSidebar />
      </PageSidebar>
      <PageBody>
        <Stack direction="column" flexGrow={1} gap={2} p={2}>
          <Routes>
            {routes.map(({ path, component, index }) => (
              <Route index={index} path={path} element={component} key={path ?? 'index'} />
            ))}
          </Routes>
        </Stack>
      </PageBody>
    </PageLayout>
  );
}

export default function AccommodationPropertyDetailsPage() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [layout, setLayout] = useAccommodationPropertyLayoutPreference();
  const basePath = `/accommodation/properties/${propertyId}`;

  const getPropertyFetch = useCallback(() => getProperty({ propertyId }), [propertyId]);

  const { fetchReqState: propertyFetchState, refetch: refetchProperty } = useAccommodationServiceFetch({
    fetchFn: getPropertyFetch,
  });

  const sharedRoutes: Array<{ path?: string; component: JSX.Element; index?: true }> = [
    {
      path: 'suppliers',
      component: <AccommodationPropertySuppliersSection refreshProperty={refetchProperty} />,
    },
    {
      path: 'room-mapping',
      component: <AccommodationPropertyRoomMappingSection />,
    },
    {
      path: 'rates',
      component: (
        <AccommodationPropertyRatesSection
          variant={layout === ACCOMMODATION_PROPERTY_LAYOUTS.SIDEBAR ? 'sidebar' : 'classic'}
        />
      ),
    },
    { path: 'facilities', component: <AccommodationPropertyFacilitiesSection /> },
    {
      path: 'room-amenities',
      component: <AccommodationPropertyRoomAmenitiesSection />,
    },
    {
      path: 'property-images',
      component: <AccommodationPropertyImagesSection />,
    },
    {
      path: 'room-images',
      component: <AccommodationPropertyRoomImagesSection />,
    },
  ];

  const classicRoutes: Array<{ path?: string; component: JSX.Element; index?: true }> = [
    { index: true, component: <AccommodationPropertyDetailsSection /> },
    { path: 'data', component: <Navigate to={basePath} replace /> },
    { path: 'generated-content', component: <Navigate to={basePath} replace /> },
    { path: 'content', component: <Navigate to={basePath} replace /> },
    { path: 'rooms', component: <AccommodationPropertyRoomsSection /> },
    { path: 'rooms/:roomId/*', component: <Navigate to={`${basePath}/rooms`} replace /> },
    ...sharedRoutes,
  ];

  const sidebarRoutes: Array<{ path?: string; component: JSX.Element; index?: true }> = [
    { index: true, component: <Navigate to="data" replace /> },
    { path: 'data', component: <AccommodationPropertyDetailsOverviewPane variant="sidebar" /> },
    { path: 'generated-content', component: <AccommodationPropertyGeneratedContent /> },
    { path: 'content', component: <AccommodationPropertyContentSelection /> },
    { path: 'rooms', component: <AccommodationPropertyRoomsSection rowClickAction="navigate" /> },
    { path: 'rooms/:roomId', component: <AccommodationPropertyRoomDetailsPage /> },
    { path: 'rooms/:roomId/:roomTab', component: <AccommodationPropertyRoomDetailsPage /> },
    ...sharedRoutes,
  ];

  const getPropertyContentFetch = useCallback(() => getPropertyContent({ propertyId }), [propertyId]);
  const { fetchReqState: contentFetchState, refetch: refetchContent } = useAccommodationServiceFetch({
    fetchFn: getPropertyContentFetch,
  });

  const handleLayoutChange = (nextLayout: AccommodationPropertyLayout) => {
    setLayout(nextLayout);

    if (nextLayout === ACCOMMODATION_PROPERTY_LAYOUTS.SIDEBAR) {
      if (pathname === basePath) {
        navigate(`${basePath}/data`);
      }
      return;
    }

    if (
      pathname === `${basePath}/data` ||
      pathname === `${basePath}/generated-content` ||
      pathname === `${basePath}/content`
    ) {
      navigate(basePath);
      return;
    }

    if (pathname.startsWith(`${basePath}/rooms/`)) {
      navigate(`${basePath}/rooms`);
    }
  };

  if (propertyFetchState.status === 'pending' || propertyFetchState.status === 'uninitiated') {
    return (
      <PageLayout hasLeftSidebar>
        <PageHeaderBar title="Property" />
        <PageBody>
          <Box p={2} display="flex" justifyContent="center">
            <CircularProgress variant="indeterminate" />
          </Box>
        </PageBody>
      </PageLayout>
    );
  }

  if (propertyFetchState.status === 'failed') {
    return (
      <PageLayout hasLeftSidebar>
        <PageHeaderBar title="Property" />
        <PageBody>
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                variant="outlined"
                onClick={() => {
                  refetchProperty();
                }}
              >
                Retry
              </Button>
            }
          >
            <AlertTitle>Failed to fetch the property.</AlertTitle>
            {JSON.stringify(propertyFetchState.error)}
          </Alert>
        </PageBody>
      </PageLayout>
    );
  }

  if (contentFetchState.status === 'pending' || contentFetchState.status === 'uninitiated') {
    return (
      <PageLayout hasLeftSidebar>
        <PageHeaderBar title={propertyFetchState.result.name || 'Property'} />
        <PageBody>
          <Box p={2} display="flex" justifyContent="center">
            <CircularProgress variant="indeterminate" />
          </Box>
        </PageBody>
      </PageLayout>
    );
  }

  if (contentFetchState.status === 'failed') {
    return (
      <PageLayout hasLeftSidebar>
        <PageHeaderBar title={propertyFetchState.result.name || 'Property'} />
        <PageBody>
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                variant="outlined"
                onClick={() => {
                  refetchContent();
                }}
              >
                Retry
              </Button>
            }
          >
            <AlertTitle>Failed to fetch the property content.</AlertTitle>
            {JSON.stringify(contentFetchState.error)}
          </Alert>
        </PageBody>
      </PageLayout>
    );
  }

  return (
    <AccommodationPropertyDetailsContextProvider details={propertyFetchState.result} content={contentFetchState.result}>
      <Helmet>
        <title>Property | {propertyFetchState.result.name || ''}</title>
      </Helmet>
      {layout === ACCOMMODATION_PROPERTY_LAYOUTS.CLASSIC ? (
        <ClassicPropertyLayout
          property={propertyFetchState.result}
          routes={classicRoutes}
          layout={layout}
          onLayoutChange={handleLayoutChange}
        />
      ) : (
        <SidebarPropertyLayout
          property={propertyFetchState.result}
          routes={sidebarRoutes}
          layout={layout}
          onLayoutChange={handleLayoutChange}
        />
      )}
    </AccommodationPropertyDetailsContextProvider>
  );
}
